/* eslint-disable import/no-cycle */
import React, { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import { navigate } from '@gatsbyjs/reach-router/';
import { useGlobal, globalizationPath } from '@rcg/ui-react';
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { FormLogin } from '../../containers';
import { Header, JiraIssueCollector } from '../../components';
import { useGlobalLogin } from '../../providers/GlobalLoginProvider';
import { useStyles } from './index.style';
import {
  IS_LOGGED_IN,
  GET_SHIPS,
  RETRIEVE_GUEST_PROFILE_BOOKING,
  RETRIEVE_BOOKING,
} from '../../graphql/queries';
import useLdFlags from '../../hooks/useLdFlags';
import defaultNotifications from '../index.defaults';
import Loader from '../../components/Loader/index';
import { trackFindReservationCTA, getGraphQLError, trackPageView } from '../../commons';
import routeUser from '../../Navigation';
import {
  FLIGHT_WARNING,
  INVALID_SAILING,
  BOOKING_TOKEN,
  AMEND_ORIGIN,
  INTERNAL_SERVER_ERROR,
  COOKIE_KEYS,
  PROFILE_BOOKING_DATA,
  BOOKING_ID_URL,
  MAIN_PROFILE_BOOKING_ID,
  ROOT_PATH,
  GA_PROFILE_BOOKINGS_LENGTH,
  LOCKED_ERROR,
  SESSION_STORAGE_GUEST_ACCOUNT_LOGGED,
  DASHBOARD_PATH,
} from '../../commons/constants';
import {
  setSessionStorageItem,
  getCookies,
  getSessionStorageItem,
  removeSessionStorageItem,
  safeDecode,
} from '../../commons/utils';
import setCurrentBookingVar from '../../graphql/operations/localState/setCurrentBooking';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../commons/dictionary';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../commons/useCommonStyles';

const getProfileData = (profileData, profileError, gaProfileBookingsLength, profileLoading) => {
  const isShortBookingFilled =
    (profileData?.retrieveBookingByGAProfile === null || gaProfileBookingsLength === 0) &&
    !profileError?.message.includes(LOCKED_ERROR);

  const isProfileDataEmpty =
    !profileData?.retrieveBookingByGAProfile?.shortProfileBooking?.length === 0;

  const hideForm = profileLoading || isProfileDataEmpty;

  return { isShortBookingFilled, hideForm };
};
const getUniqueShips = (ships) =>
  ships?.reduce((accumulator, currentShip) => {
    const nameLowerCase = currentShip.name.toLowerCase();
    const existingShip = accumulator.find((ship) => ship.name.toLowerCase() === nameLowerCase);
    if (!existingShip) {
      accumulator.push(currentShip);
    }
    return accumulator;
  }, []);
const Retrieve = () => {
  const getDictionaryValue = useDictionary(Dictionary);
  document.body.style.backgroundColor = '#F7F8F9';
  const GLOBALS = useGlobal();
  const { isAuthenticated } = useGlobalLogin();
  const {
    renderContent,
    data: { market, lang },
  } = useGlobal();
  const [headerState, headerFunc] = useState({ isPanelOpen: false });
  const [displayLoader, setDisplayLoader] = useState(true);
  const [redirectLoading, setRedirectLoading] = useState(false);
  const [guestName, setGuestName] = useState(null);
  const [profileAccessToken, setProfileAccessToken] = useState(null);
  const [triggerGuestProfile, setTriggerGuestProfile] = useState(false);
  const [newLoader, setNewLoader] = useState(false);
  const [propNotification, setNotification] = useState({
    description: '',
    open: false,
    title: '',
    type: '',
  });
  const classes = useStyles();
  const styles = useCommonStyles();
  const currency = useRef();
  const invalidFlightProfile = useRef();
  const invalidSailing = useRef();
  const client = useApolloClient();
  let retrieveError;
  const urlPath = globalizationPath(market, lang, ROOT_PATH);

  const gaProfileBookingsLength =
    getSessionStorageItem(GA_PROFILE_BOOKINGS_LENGTH) === null
      ? 1
      : getSessionStorageItem(GA_PROFILE_BOOKINGS_LENGTH);

  const [login, { called, data: retrieveBookingData, error, loading }] = useLazyQuery(
    RETRIEVE_BOOKING,
    {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      onError: (err) => {
        retrieveError = err;
      },
    },
  );

  const [loginProfile, { loading: profileLoading, data: profileData, error: profileError }] =
    useLazyQuery(RETRIEVE_GUEST_PROFILE_BOOKING, {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      onError: (err) => {
        retrieveError = err;
      },
    });

  const { ldFlags } = useLdFlags({});

  const loadRouteState = (booking) => {
    client.writeQuery({ query: IS_LOGGED_IN, data: { isLoggedIn: true } });
    setRedirectLoading(true);
    routeUser({ isLoggedIn: true }, GLOBALS, ldFlags, {
      state: {
        booking,
        invalidFlightProfile: invalidFlightProfile.current,
        invalidSailing: invalidSailing.current,
      },
    });
  };

  const errorType = (booking) => {
    if (booking.status && booking.status === 'CANCELLED') return 'InvalidStatus';
    return null;
  };

  const {
    loading: shipLoading,
    data: shipData,
    error: shipError,
  } = useQuery(GET_SHIPS, { errorPolicy: 'all', fetchPolicy: 'cache-first' });

  const makeRetrieveBookingLogin = (dataLogin) => {
    removeSessionStorageItem(BOOKING_TOKEN);
    login(dataLogin);
  };

  const makeProfileRetrieveBookingLogin = (variables) => {
    loginProfile({ variables });
  };

  useEffect(() => {
    setDisplayLoader(shipLoading);
  }, [shipLoading]);

  if (!retrieveError) {
    retrieveError = error;
  }

  useEffect(() => {
    const profile = getCookies(COOKIE_KEYS.ACCESS_TOKEN);
    if (profile) {
      setProfileAccessToken(profile);
      setSessionStorageItem(SESSION_STORAGE_GUEST_ACCOUNT_LOGGED, true);
    }
  }, [triggerGuestProfile, isAuthenticated]);

  useEffect(() => {
    const keepAliveCookie = getCookies(COOKIE_KEYS.KEEP_ALIVE);
    if (profileAccessToken && !keepAliveCookie && gaProfileBookingsLength) {
      const profileBookingId = getSessionStorageItem(BOOKING_ID_URL);
      const existingToken = getSessionStorageItem(BOOKING_TOKEN, false);
      const variables = {
        accessToken: profileAccessToken,
        bookingId: profileBookingId,
        ...(existingToken && { token: existingToken }),
      };
      makeProfileRetrieveBookingLogin(variables);
    }
  }, [profileAccessToken]);

  useEffect(() => {
    // Track view when page loads
    const profile = getCookies(COOKIE_KEYS.ACCESS_TOKEN);
    trackPageView({
      currency: currency.current,
      isGALoggedIn: Boolean(profile),
    });
  }, []);

  useEffect(() => {
    invalidSailing.current = false;
    if (!profileLoading && profileError) {
      const errorObj = getGraphQLError(profileError);
      invalidSailing.current = INVALID_SAILING === errorObj.type;
    }

    if (!profileData?.retrieveBookingByGAProfile?.shortProfileBooking) return;

    const { shortProfileBooking, booking } = profileData.retrieveBookingByGAProfile;
    setSessionStorageItem(PROFILE_BOOKING_DATA, shortProfileBooking);
    const bookingUrlToken =
      booking?.id && shortProfileBooking.find((item) => item.bookingId === booking?.id);

    const mainToken = getSessionStorageItem('main-token');

    const token = bookingUrlToken?.token || mainToken || shortProfileBooking[0].token;
    const bookingId =
      bookingUrlToken?.bookingId ||
      booking?.bookingId ||
      getSessionStorageItem(MAIN_PROFILE_BOOKING_ID) ||
      null;

    client.writeQuery({ query: IS_LOGGED_IN, data: { isLoggedIn: true } });
    setSessionStorageItem(BOOKING_TOKEN, token);
    setSessionStorageItem(MAIN_PROFILE_BOOKING_ID, bookingId);
    navigate(`${urlPath}/${DASHBOARD_PATH}`);
  }, [profileData]);

  useEffect(() => {
    if (shipError || profileError) {
      if (profileError?.message.includes(LOCKED_ERROR)) {
        setNotification({
          title: renderContent(
            'formBookingAmendErrorKeyBookingLocked',
            'Booking temporarily locked',
          ),
          description: renderContent(
            'formBookingAmendErrorMessageBookingLocked',
            'We’re sorry, it looks like your booking is temporarily locked. Please wait 30 minutes before trying again.',
          ),
          open: true,
          type: 'error',
        });
        return;
      }
      if (!profileError?.message.includes('WARNING')) {
        setNotification({
          description: renderContent(
            defaultNotifications[INTERNAL_SERVER_ERROR].description.key,
            defaultNotifications[INTERNAL_SERVER_ERROR].description.defaultValue,
          ),
          open: true,
          title: renderContent(
            defaultNotifications[INTERNAL_SERVER_ERROR].title.key,
            defaultNotifications[INTERNAL_SERVER_ERROR].title.defaultValue,
          ),
          type: 'error',
        });
      }
    }
  }, [shipError, profileError]);

  useEffect(() => {
    invalidFlightProfile.current = false;
    if (called && !loading) {
      if (retrieveError) {
        const errorObj = getGraphQLError(retrieveError);
        const errorComparison = FLIGHT_WARNING === errorObj.type;
        invalidFlightProfile.current = errorComparison;
        if (!invalidFlightProfile.current && !propNotification.open) {
          setNotification({
            description: renderContent(errorObj.description.key, errorObj.description.defaultValue),
            open: true,
            title: renderContent(errorObj.title.key, errorObj.title.defaultValue),
            type: 'error',
          });
          trackFindReservationCTA(errorObj.trackingMsg, 'error');
        }
      }
    }
  }, [retrieveBookingData, called, loading]);

  useEffect(() => {
    if (retrieveBookingData?.retrieveBooking) {
      const { booking } = retrieveBookingData.retrieveBooking;
      const bookingErrorType = errorType(booking);
      if (!bookingErrorType) {
        setCurrentBookingVar(booking);
        const { token } = retrieveBookingData.retrieveBooking;
        sessionStorage.setItem(BOOKING_TOKEN, safeDecode(token));
        sessionStorage.setItem(AMEND_ORIGIN, 'true');
        sessionStorage.setItem('retrieve', 'true');
        currency.current = booking?.price?.currencyCode;
        client.writeQuery({ query: IS_LOGGED_IN, data: { isLoggedIn: true } });
        const reservationChannel =
          booking.type && `${booking.type?.toUpperCase()}-${booking.countryCode?.toLowerCase()}`;
        trackFindReservationCTA('', 'success', currency.current, reservationChannel);
        loadRouteState(booking);
      } else {
        setNotification({
          description: renderContent(
            defaultNotifications[bookingErrorType].description.key,
            defaultNotifications[bookingErrorType].description.defaultValue,
          ),
          open: true,
          title: renderContent(
            defaultNotifications[bookingErrorType].title.key,
            defaultNotifications[bookingErrorType].title.defaultValue,
          ),
          type: 'error',
        });
        trackFindReservationCTA(defaultNotifications[bookingErrorType].trackingMsg, 'error');
      }
    }
  }, [retrieveBookingData]);

  useEffect(() => {
    setDisplayLoader(loading);
  }, [loading]);

  useEffect(() => {
    if (propNotification.open) {
      document.querySelector('.rootContainer').scrollTo(0, 0);
    }
  }, [propNotification]);

  useEffect(() => {
    setDisplayLoader(profileLoading);
    setNewLoader(profileLoading);
  }, [profileLoading]);

  const searchSailings = shipData && shipData.searchSailings;

  const ships = shipData?.ships;
  const uniqueShips = getUniqueShips(ships);

  const { isShortBookingFilled, hideForm } = getProfileData(
    profileData,
    profileError,
    gaProfileBookingsLength,
  );
  const isAvaiblableJiraWidget = ldFlags ? ldFlags['enable-jira-widget'] : null;

  return (
    <div className={`${classes.root} rootContainer`}>
      {isAvaiblableJiraWidget && <JiraIssueCollector />}
      <Loader
        display={displayLoader || shipLoading || profileLoading || redirectLoading}
        hideBackground={newLoader}
        message={newLoader && getDictionaryValue(Dictionary.amendRetrievePageLoaderMessage.key)}
      />
      <Helmet>
        <title>
          {renderContent(
            'formBookingAmendPageTitle',
            'Royal Caribbean Reservation | Manage Your Cruise Booking',
          )}
        </title>
        <meta
          name="description"
          content={renderContent(
            'formBookingAmendPageDescription',
            'Look up and manage your Royal Caribbean cruise reservation. Sign in or enter your reservation number for easy access to your booking details!',
          )}
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {!hideForm && (
        <Header
          className="header"
          title={renderContent('headerBookingAmendWelcome', 'Welcome!')}
          backLabel={renderContent('headerBookingAmendBackLabel', 'Back')}
          headerFunc={headerFunc}
          headerState={headerState}
          currency={currency.current}
          isProfileDataEmpty={isShortBookingFilled}
          guestName={guestName}
        />
      )}
      {propNotification.open && (
        <div className={`${classes.notificationContainer} ${styles.notification}`}>
          <NotificationBannerComponent
            {...propNotification}
            renderCloseBtn
            isBannerOpen
            onClose={() => setNotification({ open: false })}
          />
        </div>
      )}
      {!hideForm && (
        <FormLogin
          setTriggerGuestProfile={setTriggerGuestProfile}
          login={makeRetrieveBookingLogin}
          formTitle={renderContent('formBookingAmendSectionTitle', 'Look up my reservation')}
          searchSailings={searchSailings || undefined}
          ships={uniqueShips || undefined}
          isProfileDataEmpty={isShortBookingFilled}
          setNotification={setNotification}
          setGuestName={setGuestName}
        />
      )}
      <div className={classes.leftPadding}>{sessionStorage.getItem('eslEnv')}</div>
    </div>
  );
};

export default Retrieve;
